import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import { getProductPageRegex } from "web/script/routing/routes";
import { BrowserHistoryEvent } from "web/script/utils/history";
import logging from "web/script/utils/logging";
import storage from "web/script/utils/storage";

type OverlayDecision = "open" | "close" | "reload";

export function getOverlayDecision(
    url: URL,
    urlData: BrowserHistoryEvent | null,
    isOverlayOpen: boolean,
    isOnFeed: boolean
): OverlayDecision | void {
    // if nothing has changed in the URL, do nothing
    if (
        urlData?.newURL.pathname === urlData?.oldURL?.pathname &&
        urlData?.queryChanges?.length === 0 &&
        urlData.newURL.hash === urlData.oldURL?.hash
    ) {
        return;
    }

    const initialPageURL = storage.get("initialPageURL", null, true)
        ? new URL(storage.get("initialPageURL", "", true))
        : null;

    // handle landing on a hash URL
    if (!isOverlayOpen && url.hash.startsWith("#slug=") && url.href === initialPageURL?.href) {
        return "open";
    }

    if (!urlData) {
        return;
    }

    // ORG-3465: More details in https://confluence.lystit.com/display/OA/FY23+Q2+-+Hash+URL+Crawl+Optimisation
    // ORG-3619: More details in https://confluence.lystit.com/pages/viewpage.action?pageId=80321368
    const isGoingToProductCardHashURL = urlData.newURL.hash?.startsWith("#slug=");
    const isComingFromProductCardHashURL = urlData.oldURL?.hash?.startsWith("#slug=");

    const productPageRoute = getProductPageRegex();

    const isOpeningProductOnFeed =
        productPageRoute.test(urlData.newURL.pathname) || isGoingToProductCardHashURL;
    const isClosingProductOnFeed =
        productPageRoute.test(urlData.oldURL?.pathname || "") || isComingFromProductCardHashURL;

    if (!isOnFeed) {
        if (
            isGoingToProductCardHashURL ||
            (isComingFromProductCardHashURL &&
                urlData.eventType === "pushstate" &&
                urlData.newURL.pathname !== initialPageURL?.pathname)
        ) {
            return "open";
        }

        if (
            isComingFromProductCardHashURL &&
            (urlData.eventType === "popstate" ||
                urlData.newURL.pathname === initialPageURL?.pathname)
        ) {
            return "close";
        }

        if (isOverlayOpen) {
            return "open";
        }

        if (urlData.eventType === "popstate") {
            return "reload";
        }
    } else if (isOnFeed) {
        if (isOpeningProductOnFeed) {
            return "open";
        }

        if (isClosingProductOnFeed) {
            return "close";
        }

        // Don't send page view events on `Show more` click on Feeds
        if (
            urlData.state &&
            urlData.oldState &&
            urlData.state.pageViewId === urlData.oldState.pageViewId
        ) {
            return;
        }

        // Catch all return. Avoids reload on filter change.
        // Closely matches the functionality of _onHistoryChange in /media/web/script/components/lyst-app.js
        return;
    }

    // fallback, this shouldn't happen
    logging.error(`Unexpected page transition to ${urlData.newURL.pathname}?!`);
    return "reload";
}

export function handlePageAnalytics(productData): void {
    const overlayOnInitialPageLoad = storage.get("overlayOnInitialPageLoad", false, true);
    const initialPageURL = storage.get("initialPageURL", "", true);

    // ORG-3513: Correctly set up analytics data
    // if the initial page load opens product overlay
    // see https://confluence.lystit.com/display/OA/FY23+Q2+-+Hash+URL+Crawl+Optimisation for more details
    if (overlayOnInitialPageLoad && initialPageURL === window.location.href) {
        analytics.setInitialPageType("product_overlay");
        analytics.setInitialSubPageType(`${productData?.in_stock ? "in_stock" : "out_of_stock"}`);
    }

    const {
        ga_variables: gaVariables,
        product_analytics_data: analyticsProduct,
        product_page_view_analytics: pageViewAnalytics,
        product_url: productURL,
    } = productData;

    // update environment variables
    environment.set({
        pageType: "product_overlay",
        pageSubType: productData.in_stock ? "in_stock" : "out_of_stock",
        gaVariables,
        analyticsProduct,
        pageViewAnalytics,
        productURL,
    });

    analytics.pageView();
}
